import moment from 'moment'
import { get } from 'lodash'
import {
  CFEvent,
  CFOffer,
  CFOccurrence,
  CFCentre,
  CFCallToAction,
  CFEntry,
} from '@scentregroup/shared/types/contentful'
import {
  MDate,
  WhatsOnEvent,
  WhatsOnOffer,
  WhatsOnEventOccurrence,
  WhatsOnOfferOccurrence,
  WhatsOnImage,
} from '@scentregroup/shared/types/whats-on'
import { timezonelessDate, timezonelessTime } from '../../helpers/time-ranges'
import getImageFromEntry from '../../helpers/get-image-from-entry'
import { ImageTypes } from '@scentregroup/shared/types/images'
import getDescriptiveLocation from '../../helpers/get-descriptive-location'
import { getStoreTitle } from '../../helpers/get-store-title'
import {
  WHATS_ON_EVENT_NEXT_DAY,
  WHATS_ON_EVENT_NEXT_WEEK,
  WHATS_ON_EVENT_SAME_DAY,
  WHATS_ON_EVENT_SAME_ELSE,
} from '../../constants'

const TIME_IN_15_DAYS = 1299608404

const getImage = (item: CFEvent | CFOffer): WhatsOnImage => {
  const imageEntry = getImageFromEntry(item, ImageTypes.Square)
  return {
    sourceDesktop: imageEntry.src || '',
    sourceMobile: imageEntry.src || '',
    description: imageEntry.alt || '',
  }
}

const getEventLocation = (item: CFEvent, centre: CFCentre): string =>
  getDescriptiveLocation({
    level: item.level,
    physicalAddress: item.physicalAddress,
    hosted: item.hosted,
    storeTitle: getStoreTitle(centre, item),
  })

const getOfferLocation = (item: CFOffer, centre: CFCentre): string =>
  getDescriptiveLocation({
    storeTitle: getStoreTitle(centre, item),
  })

const CF_PREFIX_LENGTH = 2
const getCategory = (item: CFEntry): string =>
  item.__typename.substring(CF_PREFIX_LENGTH).toLowerCase()

const getSlug = (item: CFEvent | CFOffer, centre: CFCentre): string =>
  `/${centre.slug}/${getCategory(item)}/${item.sys.id}/${item.slug}`

const getBookingUrl = (item: CFEvent): string => {
  const bookingCta = get(item, 'callsToActionCollection.items', []).find(
    (cta: CFCallToAction) => cta.category === 'Book now'
  )
  return get(bookingCta, 'address', '')
}

const getTitleCollection = (
  collectionName: string,
  entry: CFEvent | CFOffer
): string[] =>
  get(entry, [collectionName, 'items'], []).map(
    (item: { title: string }) => item.title
  )

const getExperiences = (entry: CFEvent | CFOffer): string[] =>
  getTitleCollection('experiencesCollection', entry)

const getTopics = (entry: CFEvent | CFOffer): string[] =>
  getTitleCollection('topicsCollection', entry)

const eventTimeFromCFOccurrence = (
  baseDate: MDate,
  occurrence: CFOccurrence
): string => {
  const humanStartAt = moment(timezonelessTime(occurrence.startAt))
    .utc()
    .calendar(baseDate, {
      sameDay: WHATS_ON_EVENT_SAME_DAY,
      nextDay: WHATS_ON_EVENT_NEXT_DAY,
      nextWeek: WHATS_ON_EVENT_NEXT_WEEK,
      sameElse: WHATS_ON_EVENT_SAME_ELSE,
    })
  const humanEndAt = moment(timezonelessTime(occurrence.endAt))
    .utc()
    .format('h:mma')
  return `${humanStartAt} - ${humanEndAt}`
}

const getEventPrice = (event: CFEvent): string => event.priceDescription || ''

const whatsOnEventItemFromCFEvent = (
  baseDate: MDate,
  event: CFEvent,
  occurrence: CFOccurrence,
  centre: CFCentre
): WhatsOnEvent => ({
  name: event.title,
  time: eventTimeFromCFOccurrence(baseDate, occurrence),
  slug: getSlug(event, centre),
  location: getEventLocation(event, centre),
  price: getEventPrice(event),
  bookingUrl: getBookingUrl(event),
  image: getImage(event),
  experiences: getExperiences(event),
  topics: getTopics(event),
  contentTypes: ['Events'], // to simplify filtering
  analyticsMetadata: event.analyticsMetadata,
  memberExclusive: event.memberExclusive,
  highlightAttribute: event.highlightAttribute,
})

const offerDatesFromCFOffer = (baseDate: MDate, cfOffer: CFOffer): string => {
  const humanStart = timezonelessDate(cfOffer.startAt)
    .utc()
    .format('ddd DD MMM YYYY')
  const humanEnd = timezonelessDate(cfOffer.endAt)
    .utc()
    .format('ddd DD MMM YYYY')

  if (humanStart === humanEnd) {
    const humanStartAt = timezonelessDate(cfOffer.startAt)
      .utc()
      .calendar(baseDate, {
        sameDay: WHATS_ON_EVENT_SAME_DAY,
        nextDay: WHATS_ON_EVENT_NEXT_DAY,
        nextWeek: WHATS_ON_EVENT_NEXT_WEEK,
        sameElse: WHATS_ON_EVENT_SAME_ELSE,
      })
    const humanEndAt = timezonelessDate(cfOffer.endAt).utc().format('h:mma')
    return `${humanStartAt} - ${humanEndAt}`
  } else {
    return `${humanStart} - ${humanEnd}`
  }
}

const whatsOnOfferItemFromCFOffer = (
  baseDate: MDate,
  offer: CFOffer,
  centre: CFCentre
): WhatsOnOffer => ({
  name: offer.title,
  dates: offerDatesFromCFOffer(baseDate, offer),
  slug: getSlug(offer, centre),
  location: getOfferLocation(offer, centre),
  image: getImage(offer),
  timezonelessShowAtTimestamp: timezonelessDate(offer.showAt).valueOf(),
  experiences: getExperiences(offer),
  topics: getTopics(offer),
  contentTypes: ['Offers'], // to simplify filtering
  analyticsMetadata: offer.analyticsMetadata,
  memberExclusive: offer.memberExclusive,
  highlightAttribute: offer.highlightAttribute,
})

const datesInRange = (
  baseDate: MDate,
  startDate: MDate,
  endDate: MDate
): MDate[] => {
  const timeInDay = moment.duration(1, 'd').asMilliseconds()
  let timeBetweenDates = 0
  const tempDate = startDate < baseDate ? baseDate : startDate
  timeBetweenDates = endDate.valueOf() - tempDate.valueOf()
  if (timeBetweenDates > TIME_IN_15_DAYS) {
    timeBetweenDates = TIME_IN_15_DAYS
  }
  const dates = []
  for (let i = 0; i <= timeBetweenDates; i += timeInDay) {
    dates.push(moment(tempDate).utc().add(i, 'ms'))
  }
  return dates
}

export const cfEventToEventOccurrences = (
  baseDate: MDate,
  centre: CFCentre,
  cfEvent: CFEvent
): WhatsOnEventOccurrence[] =>
  cfEvent.occurrencesCollection.items.map(occurrence => ({
    item: whatsOnEventItemFromCFEvent(baseDate, cfEvent, occurrence, centre),
    startAt: timezonelessDate(occurrence.startAt),
    endAt: timezonelessDate(occurrence.endAt),
  }))

export const cfOfferToOfferOccurrences = (
  baseDate: MDate,
  centre: CFCentre,
  cfOffer: CFOffer
): WhatsOnOfferOccurrence[] =>
  datesInRange(
    baseDate,
    timezonelessDate(cfOffer.startAt),
    timezonelessDate(cfOffer.endAt)
  ).map(date => ({
    item: whatsOnOfferItemFromCFOffer(baseDate, cfOffer, centre),
    startAt: date,
    endAt: date,
  }))
